import {DonationFrequency} from 'src/components/widget/types/DonationFrequency';
import {PaymentMethod} from 'src/components/widget/types/PaymentMethod';
import {
	DonateUrlParameters,
	UTM_QUERY_PARAM
} from 'src/components/widget/types/UrlParams';
import {GIFT_CARD_ROUTE, getSiteUrl} from 'src/constants/url';

const UTM_MEDIUM = 'donate-button-0.4'; // Update this if the major version changes
const HASH = 'donate';

interface BaseUrlParams {
	nonprofitSlug: string;
	fundraiserSlug?: string;
	fundraiserId?: string;
	noExit?: boolean;
	methods?: PaymentMethod[];
	privateNote?: string;
	publicTestimony?: string;
	utmSource?: string;
	webhookToken?: string;
	partnerMetadata?: string;
	designation?: string;
	requireShareInfo?: boolean;
	customFieldResponses?: string;
	staging?: boolean;
}

interface DonateUrlParams extends BaseUrlParams {
	frequency: DonationFrequency;
	amount: number;
}

interface DonateStocksUrlParams extends BaseUrlParams {
	stockAmount: number;
	stockSymbol: string;
}

interface DonateCryptoUrlParams extends BaseUrlParams {
	cryptoAmount: number;
	cryptoCurrency: string;
}

interface GiftCardUrlParams extends BaseUrlParams {
	giftCardCode?: string;
	redeemGiftCardInFlow?: boolean;
}

function serializeParams(
	params: Record<string, string | number | boolean | undefined>
) {
	return Object.entries(params)
		.filter(([, value]) => Boolean(value))
		.map(
			([key, value]) =>
				`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
		)
		.join('&');
}

function getBaseUrl({
	fundraiserSlug,
	nonprofitSlug,
	staging
}: Pick<BaseUrlParams, 'nonprofitSlug' | 'fundraiserSlug' | 'staging'>) {
	let baseUrl = getSiteUrl(staging) + nonprofitSlug;

	if (fundraiserSlug) {
		baseUrl += '/f/' + fundraiserSlug;
	}

	return baseUrl;
}

function getUTMParamsFromPageUrl(): Record<string, string> {
	const url = new URL(window.location.href);
	const utmParams: Record<string, string> = {};

	Object.values(UTM_QUERY_PARAM).forEach((param) => {
		const value = url.searchParams.get(param);
		if (value) {
			utmParams[param] = value;
		}
	});

	return utmParams;
}

function getBaseParams({
	methods,
	nonprofitSlug,
	noExit,
	privateNote,
	publicTestimony,
	utmSource,
	webhookToken,
	partnerMetadata,
	designation,
	requireShareInfo,
	customFieldResponses
}: Pick<
	BaseUrlParams,
	| 'nonprofitSlug'
	| 'methods'
	| 'noExit'
	| 'privateNote'
	| 'publicTestimony'
	| 'utmSource'
	| 'webhookToken'
	| 'partnerMetadata'
	| 'designation'
	| 'requireShareInfo'
	| 'customFieldResponses'
>) {
	const utmParams = getUTMParamsFromPageUrl();

	return {
		[DonateUrlParameters.METHOD]: methods?.join(','),
		[DonateUrlParameters.NO_EXIT]: noExit ?? 1,
		[DonateUrlParameters.PRIVATE_NOTE]: privateNote,
		[DonateUrlParameters.PUBLIC_TESTIMONY]: publicTestimony,
		[DonateUrlParameters.PARTNER_WEBHOOK_TOKEN]: webhookToken,
		[DonateUrlParameters.PARTNER_METADATA]: partnerMetadata,
		[DonateUrlParameters.DESIGNATION]: designation,
		[DonateUrlParameters.REQUIRE_SHARE_INFO]: requireShareInfo,
		[DonateUrlParameters.CUSTOM_FIELD_RESPONSES]: customFieldResponses,
		[UTM_QUERY_PARAM.utm_campaign]: 'donate-button',
		[UTM_QUERY_PARAM.utm_source]: utmSource ?? nonprofitSlug,
		[UTM_QUERY_PARAM.utm_medium]: UTM_MEDIUM,
		...utmParams
	};
}

export function constructBasicDonateUrl(
	// eslint-disable-next-line @typescript-eslint/ban-types
	props: Omit<DonateUrlParams, 'frequency' | 'amount'>
) {
	const baseUrl = getBaseUrl(props);
	const params = getBaseParams(props);

	const parameters = serializeParams(params);

	return `${baseUrl}?${parameters}#/${HASH}`;
}

export function constructDonateUrl({
	frequency,
	amount,
	...rest
}: DonateUrlParams) {
	// If frequency is "" or undefined, default to one-time
	frequency ||= DonationFrequency.OneTime;
	const baseUrl = getBaseUrl(rest);
	const params = getBaseParams(rest);

	const parameters = serializeParams({
		[DonateUrlParameters.FREQUENCY]: frequency,
		[DonateUrlParameters.AMOUNT]: amount,
		...params
	});

	return `${baseUrl}?${parameters}#/${HASH}`;
}

export function constructDonateStocksUrl({
	stockAmount,
	stockSymbol,
	...rest
}: DonateStocksUrlParams) {
	const baseUrl = getBaseUrl(rest);
	const params = getBaseParams(rest);

	const parameters = serializeParams({
		[DonateUrlParameters.STOCK_AMOUNT]: stockAmount,
		[DonateUrlParameters.STOCK_SYMBOL]: stockSymbol,
		...params
	});

	return `${baseUrl}?${parameters}#/${HASH}`;
}

export function constructDonateCryptoUrl({
	cryptoAmount,
	cryptoCurrency,
	...rest
}: DonateCryptoUrlParams) {
	const baseUrl = getBaseUrl(rest);
	const params = getBaseParams(rest);

	const parameters = serializeParams({
		[DonateUrlParameters.CRYPTO_AMOUNT]: cryptoAmount,
		[DonateUrlParameters.CRYPTO_CURRENCY]: cryptoCurrency,
		...params
	});

	return `${baseUrl}?${parameters}#/${HASH}`;
}

export function constructGiftCardUrl({
	redeemGiftCardInFlow,
	giftCardCode,
	...rest
}: GiftCardUrlParams) {
	if (redeemGiftCardInFlow) {
		const baseUrl = getBaseUrl(rest);
		const params = getBaseParams(rest);

		const parameters = serializeParams({
			...params,
			[DonateUrlParameters.GIFT_CARD_CODE]: giftCardCode
		});

		return `${baseUrl}?${parameters}#/${HASH}`;
	}

	const giftCardUrl = getSiteUrl(rest.staging) + GIFT_CARD_ROUTE;
	const parameters = serializeParams({
		nonprofitSlug: rest.nonprofitSlug
	});

	return `${giftCardUrl}?${parameters}`;
}
